import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PlanogramEditTooltipField } from '../../core/models/personalSettings';
import { ProductGroup } from '../models/product-group';
import { MeasurementUnitTranslatePipe } from '../../core/pipes/measurement-unit-translate.pipe';

@Injectable({
	providedIn: 'root'
})
export class PlanogramTooltipService {

	constructor(
		private translate: TranslateService,
		private measurementUnitTranslatePipe: MeasurementUnitTranslatePipe
	) {}

	buildTooltipText(
		productGroup: ProductGroup,
		dataObject: any,
		fields: Array<PlanogramEditTooltipField>,
		labelsState?: { abcTurnover?: boolean; abcReceipts?: boolean; multiplePlanograms?: boolean }
	): Observable<string> {

		const keys: Array<string> = [
			'MASTER_POSITION.TITLE',
			'COMMON.PLANOGRAM.SEQUENCE_NUMBER',
			'FACES_QTY',
			'TOTAL_PLAN_FACES_QTY',
			'RECOMMENDED_FACES_QTY',
			'PLACEMENT_DEPTH',
			'PLACEMENT_HEIGHT',
			'PRICE',
			'STOCK_QTY',
			'ON_STOCK',
			'ABC_ON_TURNOVER',
			'ABC_ON_RECEIPTS_QTY',
			'BRAND',
			'CATEGORY',
			'SUPPLIER',
			'WITHOUT_SUPPLIER',
			'WITHOUT_BRAND',
			'ASSORTMENT_TYPE',
			'PRODUCT_STATUS_TYPE'
		];

		return this.translate.get(keys).pipe(
			map((trans): string => {
				let tooltip: string = productGroup.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);

				const has: (f: PlanogramEditTooltipField) => boolean = (f: PlanogramEditTooltipField): boolean => fields?.includes(f);

				if (has('sequenceNumber')) {
					tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${productGroup.number}`;
				}

				if (has('facesQty')) {
					tooltip += `\n${trans['FACES_QTY']}: ${productGroup.FacesQty}`;
				}

				if (has('totalPlanFacesQty') && !labelsState?.multiplePlanograms) {
					tooltip += `\n${trans['TOTAL_PLAN_FACES_QTY']}: ${productGroup.product.totalPlanFacesQty ?? 0}`;
				}

				if (has('recommendedFacesQty') && productGroup.product.recommendedFacesQty) {
					tooltip += `\n${trans['RECOMMENDED_FACES_QTY']}: ${productGroup.product.recommendedFacesQty}`;
				}

				if (has('placementDepth')) {
					tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${productGroup.displayPlacementDepth}`;
				}

				if (has('placementHeight')) {
					tooltip += `\n${trans['PLACEMENT_HEIGHT']}: ${productGroup.displayPlacementHeight}`;
				}

				if (has('price')) {
					tooltip += `\n${trans['PRICE']}: ${productGroup.product.price || '0'}`;
				}

				if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'stock')) {
					tooltip += `\n${trans['ON_STOCK']} ${dataObject.stock === null || dataObject.stock === undefined ? '-' : dataObject.stock}`;
				} else if (has('stockQty') && productGroup.product.stock_qty != null) {
					tooltip += `\n${trans['STOCK_QTY']}: ${productGroup.product.stock_qty}`;
				}

				if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'brand')) {
					tooltip += `\n${trans['BRAND']}: ${dataObject.brand || trans['WITHOUT_BRAND']}`;
				} else if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'category')) {
					tooltip += `\n${trans['CATEGORY']}: ${dataObject.category || '-'}`;
				} else if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'supplier')) {
					tooltip += `\n${trans['SUPPLIER']}: ${dataObject.supplier || trans['WITHOUT_SUPPLIER']}`;
				} else if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'assortment_type')) {
					tooltip += `\n${trans['ASSORTMENT_TYPE']}: ${dataObject.assortment_type || '-'}`;
				}

				if (dataObject && Object.prototype.hasOwnProperty.call(dataObject, 'status')) {
					tooltip += `\n${trans['PRODUCT_STATUS_TYPE']}: ${dataObject.status || '-'}`;
				}

				if (productGroup.product.abc_turnover && labelsState?.abcTurnover) {
					tooltip += `\n${trans['ABC_ON_TURNOVER']}: ${productGroup.product.abc_turnover}`;
				}

				if (productGroup.product.abc_receipts && labelsState?.abcReceipts) {
					tooltip += `\n${trans['ABC_ON_RECEIPTS_QTY']}: ${productGroup.product.abc_receipts}`;
				}

				return tooltip;
			})
		);
	}

	buildViewTooltipText(
		productGroup: ProductGroup,
		dataObject: any,
		filterType: string,
		filters: { interval: number, metrics: Array<string> }
	): Observable<string> {
		const ignoreKeys = ['color_avg', 'turnover_avg', 'percent_avg', 'abc_avg', 'shop_id', 'is_loose', 'id'],
			commonList = ['color', 'product_id', 'promo_product', 'new_product'],
			filterList =
				filterType == 'abc'
					? [...commonList, 'percent', 'stand_qty']
					: filterType == 'stock'
						? [...commonList, 'stand_qty', 'stock_margin', 'avg_original_price', 'packing__qty', 'product_stock_psc', 'product_stock_kg']
						: [...commonList, 'packing__qty', 'stock', 'brand', 'supplier'];

		const keys = Object.keys(dataObject || {})
				.filter((k) => !filterList.includes(k) && !ignoreKeys.includes(k))
				.map((k) => {
					let title: string = k;
					if (k == 'profit_avg') title = 'AVG_STOCK_MARGIN';
					if (k == 'qty_avg') title = 'AVG_SALES_COUNT';
					if (k == 'receipts_qty_avg') title = 'AVG_CHECK_COUNT';
					if (k == 'stock_qty') title = 'STOCK_QTY';
					if (k == 'percent') title = 'EXCESS_LACK';
					if (k == 'product_stock_psc') title = 'STAND_STOCK';
					if (k == 'product_stock_kg') title = 'PRODUCT_STOCK_ON_SHELF_PSC';
					if (k == 'excess_or_lack_percent') title = 'EXCESS_LACK';
					if (k == 'avg_sales_per_day') title = 'SALE_PER_DAY';
					return {
						value: k,
						title: title.toUpperCase()
					};
				}),
			salesIdx = keys.findIndex((i) => i.value == 'avg_sales_per_day'),
			stockIdx = keys.findIndex((i) => i.value == 'stock_qty'),
			shelfStockIdx = keys.findIndex((i) => i.value == 'product_stock_on_shelf_days');

		const interval = String(filters.interval);
		switch (interval) {
			case '1':
				if (salesIdx !== -1) keys[salesIdx].title = 'SALE_PER_DAY';
				if (stockIdx !== -1 && filterType !== 'stock') keys[stockIdx].title = 'STOCK_DAYS';
				if (shelfStockIdx !== -1) keys[shelfStockIdx].title = 'PRODUCT_STOCK_ON_SHELF_DAYS';
				break;
			case '7':
				if (salesIdx !== -1) keys[salesIdx].title = 'SALE_PER_WEEK';
				if (stockIdx !== -1 && filterType !== 'stock') keys[stockIdx].title = 'STOCK_WEEKS';
				if (shelfStockIdx !== -1) keys[shelfStockIdx].title = 'PRODUCT_STOCK_ON_SHELF_WEEK';
				break;
			case '30':
				if (salesIdx !== -1) keys[salesIdx].title = 'SALE_PER_MONTH';
				if (stockIdx !== -1 && filterType !== 'stock') keys[stockIdx].title = 'STOCK_MONTHS';
				if (shelfStockIdx !== -1) keys[shelfStockIdx].title = 'PRODUCT_STOCK_ON_SHELF_MONTH';
				break;
		}

		return this.translate
			.get(
				[
					'COMMON.PLANOGRAM.SEQUENCE_NUMBER',
					'FACES_QTY',
					'PLACEMENT_DEPTH',
					'EXCESS_LACK',
					'STOCK_QTY',
					'ON_STOCK',
					'BRAND',
					'SUPPLIER',
					'ASSORTMENT_TYPE',
					'PRODUCT_STATUS_TYPE',
					'WITHOUT_SUPPLIER',
					'WITHOUT_BRAND',
					'AVG_PERIOD_PRICE',
					'CURRENT_PRICE',
					'MASTER_POSITION.TITLE',
					'STAND_STOCK',
					'COMMON.METRICS.PSC',
					...keys.map(key => key.title)
				],
				{ unit: this.measurementUnitTranslatePipe.transform('distance', true, 'feet') }
			)
			.pipe(map((trans) => {
				let tooltip = productGroup.product.nameWithArticle(trans['MASTER_POSITION.TITLE']);
				tooltip += `\n${trans['COMMON.PLANOGRAM.SEQUENCE_NUMBER']}: ${productGroup.number}`;

				if (dataObject && filterType == 'stock') {
					keys.forEach((key, idx) => {
						tooltip += `\n${trans[key.title.toUpperCase()]}: ${dataObject[key.value] ?? '-'}`;
					});
				} else if (dataObject && ['distribution', 'square'].includes(filterType)) {
					const tooltipKeys = filterType == 'square'
						? [filters.metrics[0], filters.metrics[1], 'stock_qty']
						: [filters.metrics[0]];

					tooltip += `\n${trans['FACES_QTY']}: ${productGroup.FacesQty}`;
					tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${productGroup.displayPlacementDepth}`;

					const interval = String(filters.interval || '1');
					keys.forEach((key) => {
						if ((tooltipKeys.includes(key.value) || key.value === 'products_qty') && key.value !== 'price') {
							let translationKey = key.title.toUpperCase();
							if (key.value === 'avg_sales_per_day' || key.value === 'sale_per_day') {
								switch (interval) {
									case '1':
										translationKey = 'SALE_PER_DAY';
										break;
									case '7':
										translationKey = 'SALE_PER_WEEK';
										break;
									case '30':
										translationKey = 'SALE_PER_MONTH';
										break;
								}
							} else if (key.value === 'stock_qty') {
								translationKey = 'STOCK_QTY';
							}
							tooltip += `\n${trans[translationKey]}: ${dataObject[key.value] ?? '-'}`;
						}
					});
					tooltip += `\n${trans['AVG_PERIOD_PRICE']}: ${dataObject.avg_price ?? '-'}`;
					tooltip += `\n${trans['CURRENT_PRICE']}: ${productGroup.product.price ?? '-'}`;
				} else {
					tooltip += `\n${trans['FACES_QTY']}: ${productGroup.FacesQty}`;
					tooltip += `\n${trans['PLACEMENT_DEPTH']}: ${productGroup.displayPlacementDepth}`;

					keys.forEach((key) => {
						let value: string = key.title.toUpperCase();
						if (value === 'STATUS') value = 'PRODUCT_STATUS_TYPE';

						if (key.value === 'product_stock_psc') {
							const baseTranslation = trans['STAND_STOCK'];
							tooltip += `\n${baseTranslation}, шт.: ${dataObject[key.value] ?? '-'}`;
						} else if (key.value === 'product_stock_kg') {
							const massUnit = this.measurementUnitTranslatePipe.transform('mass', true, 'feet');
							const baseTranslation = trans['STAND_STOCK'];
							tooltip += `\n${baseTranslation}, ${massUnit}: ${dataObject[key.value] ?? '-'}`;
						} else {
							tooltip += `\n${trans[value]}: ${dataObject[key.value] ?? '-'}`;
						}
					});

					if (dataObject.hasOwnProperty('stock')) {
						tooltip += `\n${trans['ON_STOCK']} ${dataObject.stock === null ? '-' : dataObject.stock}`;
					} else if (dataObject.hasOwnProperty('brand')) {
						tooltip += `\n${trans['BRAND']}: ${dataObject.brand || trans['WITHOUT_BRAND']}`;
					} else if (dataObject.hasOwnProperty('supplier')) {
						tooltip += `\n${trans['SUPPLIER']}: ${dataObject.supplier || trans['WITHOUT_SUPPLIER']}`;
					}
				}

				return tooltip;
			}));
	}
}


